import React, { useEffect, useState } from 'react';
import { useMain } from '../../hooks/useMain';
import ShowLoader from 'src/components/CustomComponents/ShowLoader';
import {
  Button, CircularProgress, Grid, MenuItem, Select, TextField, Dialog,
  DialogContent, IconButton, Box, Typography, Tooltip, List, ListItem,
  ListItemText, Drawer,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ImageIcon from '@mui/icons-material/Image';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CloseIcon from '@mui/icons-material/Close';

const THEME = '#6174DD';

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const STATUS_META = {
  Present: { color: '#2e7d32', bg: '#e8f5e9', label: 'Present' },
  Halfday: { color: '#f9a825', bg: '#fff8e1', label: 'Half day' },
  Absent: { color: '#c62828', bg: '#ffebee', label: 'Absent' },
  'Week Off': { color: '#6174DD', bg: '#eceefc', label: 'Week off' },
  WeekOff: { color: '#6174DD', bg: '#eceefc', label: 'Week off' },
  // Holiday: { color: '#8e24aa', bg: '#edd4f1', label: 'Holiday' },
  Leave: { color: '#ef6c00', bg: '#fff3e0', label: 'Leave' },
  Upcoming: { color: '#9e9e9e', bg: '#f5f5f5', label: '—' },
};

// Options allowed when editing a FUTURE date
const FUTURE_TYPE_OPTIONS = ['Week Off', 'Leave'];

// Options allowed when editing a PAST (or today's) date
const PAST_TYPE_OPTIONS = ['Week Off', 'Leave', 'Present', 'Halfday' , 'Absent'];

const todayStr = () => {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
};

/**
 * Unified attendance calendar (attendance + week off / holiday / leave, all in one view).
 *
 * Props:
 * - selectedStaffId, staffs, data (attendance records for the month), activeBranchId
 * - yearMonth, setYearMonth, maxYearMonth
 * - onEntryChanged: callback invoked after a calendar entry is saved/removed
 *   so the parent can refresh attendance data.
 */
const AttendanceCalendar = ({
  selectedStaffId,
  staffs,
  data,
  activeBranchId,
  yearMonth,
  setYearMonth,
  maxYearMonth,
  onEntryChanged,
}) => {
  const {
    getStaffCalendarView, setCalendarEntry, removeCalendarEntry,
    fetchStaffAppointmentsDateWise, setShowMessage,
  } = useMain();

  const [calendarData, setCalendarData] = useState([]);
  const [calLoading, setCalLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [entryType, setEntryType] = useState('Week Off');
  const [entryTitle, setEntryTitle] = useState('');
  const [savingEntry, setSavingEntry] = useState(false);

  const calMonth = Number(yearMonth.split('-')[1]);
  const calYear = Number(yearMonth.split('-')[0]);

  const fetchCalendarView = async () => {
    if (!selectedStaffId) return;
    try {
      setCalLoading(true);
      const res = await getStaffCalendarView(selectedStaffId, calMonth, calYear);
      if (res?.statusCode === 200) {
        setCalendarData(res?.data || []);
      }
    } catch (e) {
      console.error('Error fetching calendar view:', e);
    } finally {
      setCalLoading(false);
    }
  };

  useEffect(() => {
    if (selectedStaffId) {
      setSelectedDay(null);
      setEntryDialogOpen(false);
      fetchCalendarView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calMonth, calYear, selectedStaffId]);

  const goPrevCalMonth = () => {
    let m = calMonth - 1;
    let y = calYear;
    if (m < 1) { m = 12; y -= 1; }
    setYearMonth(`${y}-${String(m).padStart(2, '0')}`);
  };

  const goNextCalMonth = () => {
    const next = `${calYear}-${String(calMonth + 1).padStart(2, '0')}`;
    if (next > maxYearMonth) return;
    let m = calMonth + 1;
    let y = calYear;
    if (m > 12) { m = 1; y += 1; }
    setYearMonth(`${y}-${String(m).padStart(2, '0')}`);
  };

  const resolveDisplayStatus = (d) => {
    const isFuture = d.date > todayStr();
    if (isFuture && d.status === 'Absent') return 'Upcoming';
    return d.status && STATUS_META[d.status] ? d.status : d.status;
  };

  // A day is "future" if it's strictly after today. Today itself is treated
  // as editable with the full past-day option set, since attendance for
  // today can already exist.
  const isFutureDate = (dateStr) => dateStr > todayStr();

  const getTypeOptionsForDay = (day) => (
    day && isFutureDate(day.date) ? FUTURE_TYPE_OPTIONS : PAST_TYPE_OPTIONS
  );

  const handleDayClick = (day) => {
    setSelectedDay(day);
    const options = getTypeOptionsForDay(day);
    setEntryType(options[0]);
    setEntryTitle('');
    setEntryDialogOpen(true);
  };

  const closeEntryDialog = () => {
    setEntryDialogOpen(false);
    setSelectedDay(null);
  };

  const handleSaveEntry = async () => {
    if (!selectedDay) return;
    try {
      setSavingEntry(true);
      const res = await setCalendarEntry(selectedStaffId, {
        date: selectedDay.date,
        type: entryType,
        title: entryTitle,
        paid: entryType !== 'Leave',
      });
      if (res?.statusCode === 200 || res?.message === 'Calendar entry saved') {
        setShowMessage?.({ message: 'Calendar updated successfully', messageType: 'success' });
        closeEntryDialog();
        fetchCalendarView();
        onEntryChanged?.();
      } else {
        setShowMessage?.({ message: res?.message || 'Failed to update calendar', messageType: 'error' });
      }
    } catch (e) {
      setShowMessage?.({ message: 'Something went wrong', messageType: 'error' });
    } finally {
      setSavingEntry(false);
    }
  };

  const handleConvertToNormal = async () => {
    if (!selectedDay) return;
    try {
      setSavingEntry(true);
      const res = await removeCalendarEntry(selectedStaffId, selectedDay.date);
      if (res?.statusCode === 200 || res?.message === 'Calendar entry removed') {
        setShowMessage?.({ message: 'Converted back to normal day', messageType: 'success' });
        closeEntryDialog();
        fetchCalendarView();
        onEntryChanged?.();
      } else {
        setShowMessage?.({ message: res?.message || 'Failed to update', messageType: 'error' });
      }
    } catch (e) {
      setShowMessage?.({ message: 'Something went wrong', messageType: 'error' });
    } finally {
      setSavingEntry(false);
    }
  };

  const firstDayOffset = calendarData[0]?.day ?? 0;
  const isToday = (dateStr) => dateStr === todayStr();

  const attendanceByDate = React.useMemo(() => {
    const map = {};
    (data || []).forEach((rec) => {
      if (rec?.date) map[rec.date] = rec;
    });
    return map;
  }, [data]);

  const formatDate = (dateString) => {
    const d = new Date(dateString);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const [imagePreview, setImagePreview] = useState(null);
  const openImagePreview = (url, label) => setImagePreview({ url, label });
  const closeImagePreview = () => setImagePreview(null);

  const [appointmentDay, setAppointmentDay] = useState(null);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);

  const openAppointmentsForDay = async (date) => {
    setAppointmentDay({ date, list: [] });
    if (!selectedStaffId) return;
    const formattedDate = formatDate(date);
    const queryParams = `?staffId=${selectedStaffId}&date=${formattedDate}&branchId=${activeBranchId}`;
    try {
      setAppointmentsLoading(true);
      const res = await fetchStaffAppointmentsDateWise(queryParams);
      if (res?.statusCode === 200) {
        setAppointmentDay({ date, list: res?.data || [] });
      } else {
        setAppointmentDay({ date, list: [] });
      }
    } catch (e) {
      console.error('Error fetching appointments:', e);
      setAppointmentDay({ date, list: [] });
    } finally {
      setAppointmentsLoading(false);
    }
  };
  const closeAppointmentsForDay = () => setAppointmentDay(null);

  if (!selectedStaffId) return null;

  const currentTypeOptions = getTypeOptionsForDay(selectedDay);

  return (
    <>
      <Box
        sx={{
          mb: 3,
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: `0 12px 40px rgba(97,116,221,0.18)`,
          border: '1px solid #e6e9fb',
        }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${THEME} 0%, #4a5bc7 100%)`,
            px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.22)', borderRadius: '10px', p: 0.8, display: 'flex' }}>
              <EventAvailableIcon sx={{ color: '#fff' }} />
            </Box>
            <Box>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.2 }}>
                Attendance Calendar
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem' }}>
                {staffs.find((s) => s._id === selectedStaffId)?.name}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ p: 3, bgcolor: '#f8f9ff' }}>
          <Box
            sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5,
              bgcolor: '#fff', borderRadius: '14px', p: 1, boxShadow: '0 2px 10px rgba(97,116,221,0.08)',
            }}
          >
            <IconButton onClick={goPrevCalMonth} size="small" sx={{ bgcolor: '#f0f2ff', '&:hover': { bgcolor: '#e0e4ff' } }}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography sx={{ fontWeight: 700, color: '#3a3f5c', fontSize: '1.05rem' }}>
              {monthNames[calMonth - 1]} {calYear}
            </Typography>
            <IconButton onClick={goNextCalMonth} size="small" sx={{ bgcolor: '#f0f2ff', '&:hover': { bgcolor: '#e0e4ff' } }}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>

          {calLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: THEME }} />
            </Box>
          ) : (
            <Box sx={{ bgcolor: '#fff', borderRadius: '16px', p: 2, boxShadow: '0 4px 16px rgba(97,116,221,0.08)' }}>
              <Grid container spacing={0.5} sx={{ mb: 0.5 }}>
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                  <Grid item xs={12 / 7} key={i}>
                    <Typography align="center" variant="caption" sx={{ fontWeight: 700, color: '#a0a4c0', letterSpacing: 0.5 }}>
                      {d}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
              <Grid container spacing={0.7}>
                {Array.from({ length: firstDayOffset }).map((_, i) => (
                  <Grid item xs={12 / 7} key={`blank-${i}`} />
                ))}
                {calendarData.map((d) => {
                  const displayStatus = resolveDisplayStatus(d);
                  const meta = STATUS_META[displayStatus] || STATUS_META.Upcoming;
                  const isSelected = selectedDay?.date === d.date;
                  const todayFlag = isToday(d.date);
                  const dayNum = Number(d.date.split('-')[2]);
                  const attRec = attendanceByDate[d.date];
                  const inTime = attRec?.attendance?.inTime;
                  const outTime = attRec?.attendance?.outTime;
                  const hasTimes = displayStatus === 'Present' || displayStatus === 'Halfday';
                  const effectiveHours = attRec?.effectiveWorkInHours;
                  const punchImageUrl = attRec?.attendance?.liveImage?.url || null;

                  return (
                    <Grid item xs={12 / 7} key={d.date}>
                      <Box
                        onClick={() => handleDayClick(d)}
                        sx={{
                          cursor: 'pointer',
                          borderRadius: '12px',
                          px: 0.6, py: 1,
                          minHeight: 120,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'flex-start',
                          textAlign: 'center',
                          bgcolor: isSelected ? THEME : meta.bg,
                          border: todayFlag ? `2px solid ${THEME}` : '2px solid transparent',
                          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                          position: 'relative',
                          '&:hover': { transform: 'translateY(-2px) scale(1.04)', boxShadow: '0 4px 10px rgba(0,0,0,0.15)', zIndex: 2 },
                        }}
                      >
                        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.1, color: isSelected ? '#fff' : meta.color }}>
                          {dayNum}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.65rem', fontWeight: 600, mt: 0.3,
                            color: isSelected ? 'rgba(255,255,255,0.9)' : meta.color,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}
                        >
                          {meta.label}
                        </Typography>
                        {hasTimes && (inTime || outTime) && (
                              <>
                                <Typography
                                  sx={{
                                    fontSize: '0.70rem',
                                    fontWeight: 650,
                                    mt: 0.2,
                                    color: isSelected ? 'rgba(255,255,255,0.85)' : '#6b6f8c',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  In: {inTime || '--:--'}
                                </Typography>

                                <Typography
                                  sx={{
                                    fontSize: '0.65rem',
                                    fontWeight: 650,
                                    color: isSelected ? 'rgba(255,255,255,0.85)' : '#6b6f8c',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  Out: {outTime || '--:--'}
                                </Typography>
                              </>
                            )}
                        {effectiveHours && (
                          <Typography
                            sx={{
                              fontSize: '0.8rem', fontWeight: 700, mt: 0.1,
                              color: isSelected ? '#fff' : THEME,
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}
                          >
                            {effectiveHours}
                          </Typography>
                        )}

                        <Box
                          sx={{ display: 'flex', justifyContent: 'center', gap: 0.4, mt: 0.4 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {punchImageUrl && (
                            <Tooltip title="View photo">
                              <IconButton
                                size="small"
                                onClick={() => openImagePreview(punchImageUrl, d.date)}
                                sx={{
                                  p: 0.3,
                                  bgcolor: isSelected ? 'rgba(255,255,255,0.25)' : 'rgba(97,116,221,0.12)',
                                  '&:hover': { bgcolor: isSelected ? 'rgba(255,255,255,0.4)' : 'rgba(97,116,221,0.25)' },
                                }}
                              >
                                <ImageIcon sx={{ fontSize: '0.85rem', color: isSelected ? '#fff' : THEME }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="View appointments">
                            <IconButton
                              size="small"
                              onClick={() => openAppointmentsForDay(d.date)}
                              sx={{
                                p: 0.3,
                                bgcolor: isSelected ? 'rgba(255,255,255,0.25)' : 'rgba(97,116,221,0.12)',
                                '&:hover': { bgcolor: isSelected ? 'rgba(255,255,255,0.4)' : 'rgba(97,116,221,0.25)' },
                              }}
                            >
                              <EventNoteIcon sx={{ fontSize: '0.85rem', color: isSelected ? '#fff' : THEME }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2.5 }}>
            {['Present', 'Halfday', 'Absent', 'Week Off', 'Leave'].map((label) => (
              <Box
                key={label}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.6, bgcolor: STATUS_META[label].bg, borderRadius: '20px', px: 1.2, py: 0.4 }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: STATUS_META[label].color }} />
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: STATUS_META[label].color }}>
                  {STATUS_META[label].label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ---- Set / edit calendar entry popup ---- */}
      <Dialog open={entryDialogOpen} onClose={closeEntryDialog} maxWidth="xs" fullWidth>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, py: 1.5, background: `#fbfcff` }}>
          <Typography sx={{ color: '#0a0909', fontWeight: 700, fontSize: '0.95rem' }}>
            Set entry for {selectedDay?.date}
          </Typography>
          <IconButton onClick={closeEntryDialog} sx={{ color: '#000000' }} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ bgcolor: '#f8f9ff', p: 2.5 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <Select
                fullWidth
                value={entryType}
                onChange={(e) => setEntryType(e.target.value)}
                size="small"
                sx={{ bgcolor: '#fff', borderRadius: '10px' }}
              >
                {currentTypeOptions.map((t) => (
                  <MenuItem key={t} value={t}>{STATUS_META[t]?.label || t}</MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Title (optional)"
                value={entryTitle}
                onChange={(e) => setEntryTitle(e.target.value)}
                sx={{ bgcolor: '#fff', borderRadius: '10px' }}
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mt: 2.5 }}>
            {['Week Off', 'WeekOff', 'Leave'].includes(selectedDay?.status) ? (
              <Button
                onClick={handleConvertToNormal}
                disabled={savingEntry}
                sx={{ color: '#c62828', fontWeight: 600, textTransform: 'none', '&:hover': { bgcolor: '#ffebee' } }}
              >
                Convert to normal day
              </Button>
            ) : <span />}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={closeEntryDialog} sx={{ color: '#888' }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveEntry}
                disabled={savingEntry}
                sx={{ background: `linear-gradient(135deg, ${THEME}, #4a5bc7)`, borderRadius: '10px', px: 3 }}
              >
                {savingEntry ? 'Saving...' : 'Save entry'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ---- Punch photo lightbox ---- */}
      <Dialog open={!!imagePreview} onClose={closeImagePreview} maxWidth="xs" fullWidth>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, background: `linear-gradient(135deg, ${THEME}, #4a5bc7)` }}>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem' }}>
            Photo — {imagePreview?.label}
          </Typography>
          <IconButton onClick={closeImagePreview} sx={{ color: '#fff' }} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', bgcolor: '#f8f9ff', p: 2, minHeight: 160, alignItems: 'center' }}>
          {imagePreview?.url ? (
            <Box
              component="img"
              src={imagePreview.url}
              alt="Attendance"
              sx={{ maxWidth: '100%', maxHeight: 400, borderRadius: '12px', boxShadow: '0 4px 16px rgba(97,116,221,0.15)' }}
            />
          ) : (
            <Typography sx={{ color: '#888', fontSize: '0.85rem' }}>
              No photo available for this day.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* ---- Appointments for a day (sidebar) ---- */}
      <Drawer anchor="right" open={!!appointmentDay} onClose={closeAppointmentsForDay}>
        <Box sx={{ width: 340, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, py: 2, background: `linear-gradient(135deg, ${THEME}, #4a5bc7)` }}>
            <Box>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem' }}>
                Appointments
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem' }}>
                {appointmentDay?.date}
              </Typography>
            </Box>
            <IconButton onClick={closeAppointmentsForDay} sx={{ color: '#fff' }} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: '#f8f9ff' }}>
            {appointmentsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <ShowLoader />
              </Box>
            ) : appointmentDay?.list?.length ? (
              <List disablePadding>
                {appointmentDay.list.map((appt, idx) => (
                  <React.Fragment key={appt._id || idx}>
                    <ListItem sx={{ px: 2.5, py: 1.5, bgcolor: '#fff', mx: 1.5, my: 1, borderRadius: '12px', boxShadow: '0 2px 8px rgba(97,116,221,0.08)' }}>
                      <ListItemText
                        primary={
                          <Typography sx={{ fontWeight: 700, color: '#3a3f5c', fontSize: '0.9rem' }}>
                            {appt.clientName || `Appointment ${idx + 1}`}
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ mt: 0.3 }}>
                            <Typography sx={{ fontSize: '0.78rem', color: '#6b6f8c' }}>
                              {appt.dateOfAppointment}
                            </Typography>
                            {appt.serviceSelected?.service?.serviceName && (
                              <Typography sx={{ fontSize: '0.78rem', color: THEME, fontWeight: 600, mt: 0.2 }}>
                                {appt.serviceSelected.service.serviceName}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Typography sx={{ p: 4, textAlign: 'center', color: '#888', fontSize: '0.85rem' }}>
                No appointments selected.
              </Typography>
            )}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default AttendanceCalendar;